import { useState, useEffect, useRef } from "react";

const API_BASE = "http://localhost:8000";

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_LABELS = {
  identity_access:          "Identity & Access Management",
  network_security:         "Network Security",
  endpoint_security:        "Endpoint Security",
  endpoint_protection:      "Endpoint Protection",
  data_protection:          "Data Protection",
  incident_response:        "Incident Response",
  vulnerability_management: "Vulnerability Management",
  cloud_security:           "Cloud Security",
  application_security:     "Application Security",
  third_party_risk:         "Third-Party Risk Management",
  compliance_governance:    "Compliance & Governance",
  security_awareness:       "Security Awareness & Training",
  physical_security:        "Physical Security",
  business_continuity:      "Business Continuity",
  backup_recovery:          "Backup & Recovery",
  logging_monitoring:       "Logging & Monitoring",
  asset_management:         "Asset Management",
};

const TASK_TEMPLATES = {
  identity_access: ["Implement MFA for privileged accounts","Conduct user access review","Deploy PAM solution","Enforce least-privilege policies"],
  network_security: ["Harden firewall ruleset","Implement network segmentation","Deploy IDS/IPS","Document change management process"],
  data_protection: ["Inventory and classify sensitive data","Encrypt customer financial data at rest","Deploy DLP tooling","Establish data retention procedures"],
  incident_response: ["Develop Incident Response Plan","Conduct tabletop exercise","Establish 24/7 escalation path","Integrate IR with regulatory notifications"],
  vulnerability_management: ["Establish vulnerability scanning schedule","Define patching SLAs by severity","Integrate findings into risk register","Conduct annual penetration test"],
  cloud_security: ["Enable CSPM tooling","Review IAM policies across cloud accounts","Enable CloudTrail audit logging","Implement cloud workload protection"],
  endpoint_security: ["Deploy EDR on all endpoints","Enforce disk encryption on laptops","Implement USB controls","Establish endpoint compliance monitoring"],
  endpoint_protection: ["Deploy next-gen antivirus","Enable application whitelisting","Configure automated threat detection","Establish patch compliance monitoring"],
  application_security: ["Implement SAST/DAST in CI/CD","Conduct OWASP Top 10 review","Establish secure code review process","Deploy WAF for public-facing apps"],
  third_party_risk: ["Complete vendor inventory","Implement security questionnaire process","Review vendor contracts","Establish ongoing vendor monitoring"],
  compliance_governance: ["Develop Information Security Policy","Assign Qualified Individual","Establish Board reporting cadence","Complete regulatory gap assessment"],
  security_awareness: ["Launch mandatory security training","Conduct phishing simulation","Establish role-based training","Create security awareness metrics"],
  physical_security: ["Audit physical access controls","Implement visitor logging","Review CCTV coverage","Conduct physical security risk assessment"],
  business_continuity: ["Develop BCP documentation","Conduct annual BCP tabletop","Define and test RTOs","Establish crisis communications procedures"],
  backup_recovery: ["Verify backup completeness quarterly","Implement immutable backup","Document and test DR runbook","Establish backup monitoring"],
  logging_monitoring: ["Deploy SIEM with baseline alerting","Enable audit logging on financial systems","Establish daily log review","Configure suspicious auth alerting"],
  asset_management: ["Complete hardware/software inventory","Implement automated asset discovery","Establish lifecycle management","Define decommissioning standards"],
};

const FILE_ICONS = {
  pdf:  { icon: "📄", color: "#ef4444", label: "PDF" },
  png:  { icon: "🖼", color: "#38bdf8", label: "Image" },
  jpg:  { icon: "🖼", color: "#38bdf8", label: "Image" },
  jpeg: { icon: "🖼", color: "#38bdf8", label: "Image" },
  doc:  { icon: "📝", color: "#3b82f6", label: "Word" },
  docx: { icon: "📝", color: "#3b82f6", label: "Word" },
  xls:  { icon: "📊", color: "#22c55e", label: "Excel" },
  xlsx: { icon: "📊", color: "#22c55e", label: "Excel" },
  note: { icon: "✏️", color: "#f59e0b", label: "Note" },
};

const getFileType = (filename) => {
  const ext = filename?.split(".").pop()?.toLowerCase() || "note";
  return FILE_ICONS[ext] || { icon: "📎", color: "#64748b", label: "File" };
};

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// ─── Generate tasks from gaps (mirrors RemediationPunchList logic) ─────────────

function generateTasksFromGaps(responses) {
  const tasks = [];
  let id = 1;
  const sorted = Object.entries(responses)
    .map(([key, val]) => ({ key, gap: (val.target||5)-(val.score||0) }))
    .filter(g => g.gap > 0)
    .sort((a, b) => b.gap - a.gap);

  for (const { key } of sorted) {
    const templates = TASK_TEMPLATES[key] || [];
    for (const title of templates) {
      tasks.push({ id: `task_${id++}`, title, category: key, categoryLabel: CATEGORY_LABELS[key] || key });
    }
  }
  return tasks;
}

// ─── Add Evidence Modal ───────────────────────────────────────────────────────

function AddEvidenceModal({ tasks, categories, onAdd, onClose }) {
  const [type, setType] = useState("file"); // file | note
  const [file, setFile] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [linkedTask, setLinkedTask] = useState("");
  const [linkedCategory, setLinkedCategory] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("pending");
  const fileRef = useRef();

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); if (!title) setTitle(f.name); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); if (!title) setTitle(f.name); }
  };

  const handleSubmit = () => {
    if (!title.trim()) return;
    if (type === "file" && !file) return;
    if (type === "note" && !noteText.trim()) return;

    const item = {
      id: `ev_${Date.now()}`,
      type,
      title: title.trim(),
      description: description.trim(),
      linkedTask,
      linkedCategory,
      linkedTaskLabel: tasks.find(t => t.id === linkedTask)?.title || "",
      linkedCategoryLabel: CATEGORY_LABELS[linkedCategory] || "",
      verificationStatus,
      addedAt: new Date().toISOString(),
      addedBy: "Current User",
      ...(type === "file" ? {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      } : {
        noteText: noteText.trim(),
        fileName: null,
      }),
    };
    onAdd(item);
    onClose();
  };

  const canSubmit = title.trim() && (type === "note" ? noteText.trim() : file);

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Add Evidence</h3>
          <button style={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        <div style={styles.modalBody}>
          {/* Type Toggle */}
          <div style={styles.typeToggle}>
            {[["file","📎 File Upload"],["note","✏️ Text Note"]].map(([t, label]) => (
              <button key={t}
                style={{ ...styles.typeBtn, ...(type === t ? styles.typeBtnActive : {}) }}
                onClick={() => setType(t)}
              >{label}</button>
            ))}
          </div>

          {/* File Upload */}
          {type === "file" && (
            <div
              style={{ ...styles.dropzone, ...(file ? styles.dropzoneFilled : {}) }}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                style={{ display:"none" }} onChange={handleFileChange} />
              {file ? (
                <div style={styles.filePreview}>
                  <span style={{ fontSize:28 }}>{getFileType(file.name).icon}</span>
                  <div>
                    <div style={styles.fileName}>{file.name}</div>
                    <div style={styles.fileSize}>{formatBytes(file.size)}</div>
                  </div>
                  <button style={styles.clearFile} onClick={e => { e.stopPropagation(); setFile(null); setTitle(""); }}>✕</button>
                </div>
              ) : (
                <div style={styles.dropzoneEmpty}>
                  <div style={styles.dropzoneIcon}>⬆</div>
                  <div style={styles.dropzoneText}>Drop file here or click to browse</div>
                  <div style={styles.dropzoneHint}>PDF, PNG, JPG, DOC, DOCX, XLS, XLSX</div>
                </div>
              )}
            </div>
          )}

          {/* Note */}
          {type === "note" && (
            <textarea
              style={styles.noteArea}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Document your evidence, observations, or implementation notes here..."
              rows={5}
              autoFocus
            />
          )}

          {/* Title */}
          <div style={styles.field}>
            <label style={styles.fieldLabel}>Title *</label>
            <input style={styles.fieldInput} value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Brief description of this evidence" />
          </div>

          {/* Description */}
          <div style={styles.field}>
            <label style={styles.fieldLabel}>Description</label>
            <textarea style={styles.fieldTextarea} value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does this evidence demonstrate? How does it verify completion?"
              rows={2} />
          </div>

          <div style={styles.fieldGrid}>
            {/* Link to Task */}
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Link to Task</label>
              <select style={styles.fieldSelect} value={linkedTask} onChange={e => setLinkedTask(e.target.value)}>
                <option value="">— None —</option>
                {tasks.map(t => (
                  <option key={t.id} value={t.id}>{t.categoryLabel}: {t.title.substring(0,50)}</option>
                ))}
              </select>
            </div>

            {/* Link to Category */}
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Security Category</label>
              <select style={styles.fieldSelect} value={linkedCategory} onChange={e => setLinkedCategory(e.target.value)}>
                <option value="">— None —</option>
                {categories.map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Verification Status */}
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Verification Status</label>
              <select style={styles.fieldSelect} value={verificationStatus} onChange={e => setVerificationStatus(e.target.value)}>
                <option value="pending">Pending Review</option>
                <option value="verified">Verified</option>
                <option value="rejected">Needs Revision</option>
              </select>
            </div>
          </div>
        </div>

        <div style={styles.modalFooter}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...styles.submitBtn, opacity: canSubmit ? 1 : 0.4 }}
            onClick={handleSubmit} disabled={!canSubmit}>
            Add Evidence
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Evidence Card ────────────────────────────────────────────────────────────

const VERIFICATION_CONFIG = {
  pending:  { label: "Pending Review", color: "#f59e0b", bg: "#f59e0b15" },
  verified: { label: "Verified",       color: "#22c55e", bg: "#22c55e15" },
  rejected: { label: "Needs Revision", color: "#ef4444", bg: "#ef444415" },
};

function EvidenceCard({ item, onDelete, onUpdateStatus }) {
  const ft = getFileType(item.fileName || "note");
  const vc = VERIFICATION_CONFIG[item.verificationStatus] || VERIFICATION_CONFIG.pending;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div style={styles.evidenceCard}>
      {/* Icon */}
      <div style={{ ...styles.evidenceIcon, background: ft.color + "18", border: `1px solid ${ft.color}30` }}>
        <span style={{ fontSize: 22 }}>{ft.icon}</span>
      </div>

      {/* Content */}
      <div style={styles.evidenceContent}>
        <div style={styles.evidenceTitleRow}>
          <span style={styles.evidenceTitle}>{item.title}</span>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ ...styles.verBadge, background: vc.bg, color: vc.color }}>
              {vc.label}
            </span>
            <div style={{ position:"relative" }}>
              <button style={styles.menuBtn} onClick={() => setMenuOpen(v => !v)}>⋯</button>
              {menuOpen && (
                <div style={styles.menu}>
                  {Object.entries(VERIFICATION_CONFIG).map(([k, v]) => (
                    <button key={k} style={{ ...styles.menuItem, color: v.color }}
                      onClick={() => { onUpdateStatus(item.id, k); setMenuOpen(false); }}>
                      Mark {v.label}
                    </button>
                  ))}
                  <div style={styles.menuDivider} />
                  <button style={{ ...styles.menuItem, color:"#ef4444" }}
                    onClick={() => { onDelete(item.id); setMenuOpen(false); }}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {item.description && (
          <div style={styles.evidenceDesc}>{item.description}</div>
        )}

        {item.type === "note" && (
          <div style={styles.notePreview}>{item.noteText?.substring(0, 200)}{item.noteText?.length > 200 ? "…" : ""}</div>
        )}

        <div style={styles.evidenceMeta}>
          {item.fileName && (
            <span style={styles.metaTag}>
              <span style={{ color: ft.color }}>{ft.label}</span> · {item.fileName} {item.fileSize ? `· ${formatBytes(item.fileSize)}` : ""}
            </span>
          )}
          {item.linkedTaskLabel && (
            <span style={styles.metaTag}>🔗 {item.linkedTaskLabel.substring(0, 45)}{item.linkedTaskLabel.length > 45 ? "…" : ""}</span>
          )}
          {item.linkedCategoryLabel && (
            <span style={styles.metaTag}>📁 {item.linkedCategoryLabel}</span>
          )}
          <span style={styles.metaTag}>🕒 {formatDate(item.addedAt)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Audit Trail Entry ────────────────────────────────────────────────────────

function AuditEntry({ entry }) {
  return (
    <div style={styles.auditRow}>
      <div style={styles.auditDot} />
      <div style={styles.auditContent}>
        <span style={styles.auditAction}>{entry.action}</span>
        <span style={styles.auditMeta}> · {entry.user} · {formatDate(entry.timestamp)}</span>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EvidenceCollection() {
  const [evidence, setEvidence]     = useState([]);
  const [tasks, setTasks]           = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [filterCat, setFilterCat]   = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode]     = useState("grid"); // grid | list | audit
  const [auditLog, setAuditLog]     = useState([]);
  const [activeTab, setActiveTab]   = useState("evidence"); // evidence | audit

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const customerId = localStorage.getItem("customer_id") || "cust_techcorpin_575148";
      const res = await fetch(`${API_BASE}/api/assessments/current?customer_id=${customerId}`);
      if (!res.ok) throw new Error("Failed to load assessment");
      const data = await res.json();
      const responses = data.assessment?.responses || data.responses || {};

      const generatedTasks = generateTasksFromGaps(responses);
      setTasks(generatedTasks);

      const cats = Object.entries(responses)
        .filter(([, v]) => ((v.target||5)-(v.score||0)) > 0)
        .map(([key]) => ({ key, label: CATEGORY_LABELS[key] || key }));
      setCategories(cats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addAuditEntry = (action) => {
    setAuditLog(prev => [{
      id: Date.now(),
      action,
      user: "Current User",
      timestamp: new Date().toISOString(),
    }, ...prev]);
  };

  const handleAdd = (item) => {
    setEvidence(prev => [item, ...prev]);
    addAuditEntry(`Added evidence: "${item.title}" (${item.type === "note" ? "Text Note" : item.fileName})`);
  };

  const handleDelete = (id) => {
    const item = evidence.find(e => e.id === id);
    setEvidence(prev => prev.filter(e => e.id !== id));
    if (item) addAuditEntry(`Deleted evidence: "${item.title}"`);
  };

  const handleUpdateStatus = (id, status) => {
    const item = evidence.find(e => e.id === id);
    setEvidence(prev => prev.map(e => e.id === id ? { ...e, verificationStatus: status } : e));
    if (item) addAuditEntry(`Updated "${item.title}" status → ${VERIFICATION_CONFIG[status]?.label}`);
  };

  // ── Filter ──
  const filtered = evidence.filter(e => {
    if (filterCat !== "all" && e.linkedCategory !== filterCat) return false;
    if (filterStatus !== "all" && e.verificationStatus !== filterStatus) return false;
    return true;
  });

  // ── Stats ──
  const total    = evidence.length;
  const verified = evidence.filter(e => e.verificationStatus === "verified").length;
  const pending  = evidence.filter(e => e.verificationStatus === "pending").length;
  const rejected = evidence.filter(e => e.verificationStatus === "rejected").length;

  return (
    <div style={styles.page}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Evidence Collection</h1>
          <p style={styles.subtitle}>Upload and manage implementation evidence for compliance and audit verification</p>
        </div>
        <button style={styles.addBtn} onClick={() => setShowModal(true)}>
          + Add Evidence
        </button>
      </div>

      {/* ── Stats Strip ── */}
      <div style={styles.statsStrip}>
        {[
          { label: "Total Evidence",   value: total,    color: "#e2e8f0" },
          { label: "Verified",         value: verified, color: "#22c55e" },
          { label: "Pending Review",   value: pending,  color: "#f59e0b" },
          { label: "Needs Revision",   value: rejected, color: "#ef4444" },
          { label: "Categories Covered", value: new Set(evidence.map(e => e.linkedCategory).filter(Boolean)).size, color: "#38bdf8" },
          { label: "Tasks Evidenced",  value: new Set(evidence.map(e => e.linkedTask).filter(Boolean)).size, color: "#a78bfa" },
        ].map(s => (
          <div key={s.label} style={styles.stat}>
            <div style={{ ...styles.statValue, color: s.color }}>{s.value}</div>
            <div style={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tab Bar ── */}
      <div style={styles.tabBar}>
        <div style={styles.tabs}>
          {[["evidence","Evidence Library"],["audit","Audit Trail"]].map(([t, label]) => (
            <button key={t}
              style={{ ...styles.tab, ...(activeTab === t ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(t)}>
              {label}
              {t === "audit" && auditLog.length > 0 && (
                <span style={styles.tabCount}>{auditLog.length}</span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "evidence" && (
          <div style={styles.toolbar}>
            <select style={styles.filterSelect} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending Review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Needs Revision</option>
            </select>
            <div style={styles.viewToggle}>
              {[["grid","▦"],["list","☰"]].map(([v, icon]) => (
                <button key={v}
                  style={{ ...styles.viewBtn, ...(viewMode === v ? styles.viewBtnActive : {}) }}
                  onClick={() => setViewMode(v)}>{icon}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Content ── */}
      <div style={styles.content}>
        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : error ? (
          <div style={{ ...styles.empty, color:"#ef4444" }}>⚠ {error}</div>
        ) : activeTab === "audit" ? (
          /* Audit Trail */
          <div style={styles.auditPanel}>
            <div style={styles.auditHeader}>
              <span style={styles.auditTitle}>Complete Activity Log</span>
              <span style={styles.auditHint}>All evidence actions are recorded for compliance purposes</span>
            </div>
            {auditLog.length === 0 ? (
              <div style={styles.empty}>No activity recorded yet.</div>
            ) : (
              <div style={styles.auditList}>
                {auditLog.map(entry => <AuditEntry key={entry.id} entry={entry} />)}
              </div>
            )}
          </div>
        ) : filtered.length === 0 ? (
          /* Empty State */
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🗂</div>
            <div style={styles.emptyTitle}>
              {evidence.length === 0 ? "No evidence added yet" : "No evidence matches current filters"}
            </div>
            <div style={styles.emptyHint}>
              {evidence.length === 0
                ? "Upload documents, screenshots, or text notes to document your security improvements"
                : "Try adjusting your filters"}
            </div>
            {evidence.length === 0 && (
              <button style={styles.emptyAddBtn} onClick={() => setShowModal(true)}>
                + Add First Evidence Item
              </button>
            )}
          </div>
        ) : viewMode === "grid" ? (
          /* Grid View */
          <div style={styles.grid}>
            {filtered.map(item => (
              <EvidenceCard key={item.id} item={item}
                onDelete={handleDelete} onUpdateStatus={handleUpdateStatus} />
            ))}
          </div>
        ) : (
          /* List View */
          <div style={styles.list}>
            {filtered.map(item => (
              <EvidenceCard key={item.id} item={item}
                onDelete={handleDelete} onUpdateStatus={handleUpdateStatus} />
            ))}
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <AddEvidenceModal
          tasks={tasks}
          categories={categories}
          onAdd={handleAdd}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: "100vh",
    background: "#07090f",
    color: "#e2e8f0",
    fontFamily: "'Outfit', 'DM Sans', system-ui, sans-serif",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "30px 40px 22px", borderBottom: "1px solid #111827",
  },
  headerLeft: { display: "flex", flexDirection: "column", gap: 5 },
  badge: {
    fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
    color: "#7c3aed", background: "#7c3aed18",
    border: "1px solid #7c3aed40", borderRadius: 4,
    padding: "2px 8px", width: "fit-content",
  },
  title: { fontSize: 26, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.03em" },
  subtitle: { fontSize: 13, color: "#475569", margin: 0 },
  addBtn: {
    padding: "11px 22px",
    background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
    border: "none", borderRadius: 9,
    color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
    boxShadow: "0 4px 16px #0ea5e930",
    alignSelf: "flex-start",
  },
  statsStrip: {
    display: "flex", borderBottom: "1px solid #111827",
    background: "#08090f", overflowX: "auto",
  },
  stat: {
    display: "flex", flexDirection: "column", gap: 3,
    padding: "14px 28px", borderRight: "1px solid #111827", flexShrink: 0,
  },
  statValue: { fontSize: 22, fontWeight: 800, lineHeight: 1 },
  statLabel: { fontSize: 10, color: "#334155", letterSpacing: "0.06em", textTransform: "uppercase" },
  tabBar: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "0 40px", borderBottom: "1px solid #111827",
    background: "#06080e", flexWrap: "wrap", gap: 8,
  },
  tabs: { display: "flex" },
  tab: {
    padding: "13px 18px", background: "none",
    border: "none", borderBottom: "2px solid transparent",
    color: "#475569", fontSize: 13, fontWeight: 500, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 6,
  },
  tabActive: { color: "#38bdf8", borderBottomColor: "#0ea5e9" },
  tabCount: {
    fontSize: 10, fontWeight: 700,
    background: "#1a2236", color: "#64748b",
    borderRadius: 10, padding: "1px 6px",
  },
  toolbar: { display: "flex", gap: 10, alignItems: "center", padding: "8px 0" },
  filterSelect: {
    background: "#0f1420", border: "1px solid #1a2236",
    borderRadius: 6, padding: "6px 10px",
    color: "#94a3b8", fontSize: 12, outline: "none",
  },
  viewToggle: { display: "flex", gap: 2 },
  viewBtn: {
    width: 30, height: 30, background: "none",
    border: "1px solid #1a2236", borderRadius: 5,
    color: "#475569", fontSize: 13, cursor: "pointer",
  },
  viewBtnActive: { background: "#0ea5e920", borderColor: "#0ea5e960", color: "#38bdf8" },
  content: { padding: "24px 40px" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
    gap: 14,
  },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  empty: { color: "#475569", textAlign: "center", padding: "60px 0", fontSize: 14 },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "80px 0", gap: 12,
  },
  emptyIcon: { fontSize: 48, opacity: 0.3 },
  emptyTitle: { fontSize: 16, fontWeight: 600, color: "#64748b" },
  emptyHint: { fontSize: 13, color: "#334155", textAlign: "center", maxWidth: 380 },
  emptyAddBtn: {
    marginTop: 8, padding: "10px 20px",
    background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
    border: "none", borderRadius: 8,
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  // Evidence Card
  evidenceCard: {
    display: "flex", gap: 14,
    background: "#0c0f1a", border: "1px solid #111827",
    borderRadius: 12, padding: "16px 18px",
    transition: "border-color 0.15s",
  },
  evidenceIcon: {
    width: 48, height: 48, borderRadius: 10,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  evidenceContent: { flex: 1, display: "flex", flexDirection: "column", gap: 7, minWidth: 0 },
  evidenceTitleRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 },
  evidenceTitle: { fontSize: 14, fontWeight: 600, color: "#f1f5f9", lineHeight: 1.3 },
  evidenceDesc: { fontSize: 12, color: "#64748b", lineHeight: 1.5 },
  notePreview: {
    fontSize: 12, color: "#94a3b8", lineHeight: 1.5,
    background: "#111827", borderRadius: 6, padding: "8px 10px",
    borderLeft: "3px solid #f59e0b",
  },
  evidenceMeta: { display: "flex", flexWrap: "wrap", gap: 8 },
  metaTag: {
    fontSize: 11, color: "#475569",
    background: "#111827", borderRadius: 4, padding: "2px 7px",
  },
  verBadge: {
    fontSize: 10, fontWeight: 700, borderRadius: 5,
    padding: "3px 8px", letterSpacing: "0.04em", flexShrink: 0,
  },
  menuBtn: {
    width: 28, height: 28, background: "none",
    border: "1px solid #1a2236", borderRadius: 6,
    color: "#64748b", fontSize: 16, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  menu: {
    position: "absolute", top: "100%", right: 0, zIndex: 100,
    background: "#111827", border: "1px solid #1a2236",
    borderRadius: 8, overflow: "hidden", minWidth: 160,
    boxShadow: "0 8px 24px #00000070", marginTop: 4,
  },
  menuItem: {
    display: "block", width: "100%", padding: "9px 14px",
    background: "none", border: "none",
    fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "left",
  },
  menuDivider: { height: 1, background: "#1a2236", margin: "4px 0" },
  // Modal
  modalOverlay: {
    position: "fixed", inset: 0, background: "#00000085",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#0f1420", border: "1px solid #1a2236",
    borderRadius: 16, width: 600, maxWidth: "92vw", maxHeight: "90vh",
    overflow: "auto", boxShadow: "0 24px 60px #000000b0",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "20px 24px", borderBottom: "1px solid #1a2236",
    position: "sticky", top: 0, background: "#0f1420", zIndex: 1,
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: "#f1f5f9", margin: 0 },
  modalClose: { background: "none", border: "none", color: "#64748b", fontSize: 18, cursor: "pointer" },
  modalBody: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 },
  modalFooter: {
    display: "flex", justifyContent: "flex-end", gap: 10,
    padding: "16px 24px", borderTop: "1px solid #1a2236",
    position: "sticky", bottom: 0, background: "#0f1420",
  },
  typeToggle: { display: "flex", gap: 4, background: "#0a0d16", borderRadius: 8, padding: 4 },
  typeBtn: {
    flex: 1, padding: "8px", background: "none",
    border: "none", borderRadius: 6,
    color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  typeBtnActive: { background: "#111827", color: "#e2e8f0" },
  dropzone: {
    border: "2px dashed #1a2236", borderRadius: 10,
    padding: "28px", cursor: "pointer", transition: "all 0.2s",
    background: "#0a0d16",
  },
  dropzoneFilled: { borderColor: "#0ea5e960", background: "#0ea5e908" },
  dropzoneEmpty: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8 },
  dropzoneIcon: { fontSize: 28, color: "#334155" },
  dropzoneText: { fontSize: 13, color: "#64748b", fontWeight: 500 },
  dropzoneHint: { fontSize: 11, color: "#334155" },
  filePreview: { display: "flex", alignItems: "center", gap: 12 },
  fileName: { fontSize: 13, fontWeight: 600, color: "#e2e8f0" },
  fileSize: { fontSize: 11, color: "#475569" },
  clearFile: { marginLeft:"auto", background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:14 },
  noteArea: {
    background: "#0a0d16", border: "1px solid #1a2236",
    borderRadius: 8, padding: "12px 14px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    resize: "vertical", fontFamily: "inherit", lineHeight: 1.6,
    width: "100%", boxSizing: "border-box",
  },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: "#475569", letterSpacing: "0.08em", textTransform: "uppercase" },
  fieldInput: {
    background: "#0a0d16", border: "1px solid #1a2236",
    borderRadius: 7, padding: "9px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
  },
  fieldTextarea: {
    background: "#0a0d16", border: "1px solid #1a2236",
    borderRadius: 7, padding: "9px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
    resize: "vertical", fontFamily: "inherit", lineHeight: 1.5,
  },
  fieldSelect: {
    background: "#0a0d16", border: "1px solid #1a2236",
    borderRadius: 7, padding: "9px 12px",
    color: "#e2e8f0", fontSize: 13, outline: "none",
  },
  fieldGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
  cancelBtn: {
    padding: "9px 18px", background: "none",
    border: "1px solid #1a2236", borderRadius: 8,
    color: "#64748b", fontSize: 13, cursor: "pointer",
  },
  submitBtn: {
    padding: "9px 22px",
    background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
    border: "none", borderRadius: 8,
    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
  },
  // Audit trail
  auditPanel: {
    background: "#0c0f1a", border: "1px solid #111827",
    borderRadius: 12, padding: "24px",
  },
  auditHeader: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 20 },
  auditTitle: { fontSize: 15, fontWeight: 700, color: "#f1f5f9" },
  auditHint: { fontSize: 12, color: "#475569" },
  auditList: { display: "flex", flexDirection: "column", gap: 0 },
  auditRow: {
    display: "flex", gap: 14, alignItems: "flex-start",
    padding: "10px 0", borderBottom: "1px solid #111827",
  },
  auditDot: {
    width: 8, height: 8, borderRadius: "50%",
    background: "#0ea5e9", flexShrink: 0, marginTop: 5,
  },
  auditContent: { flex: 1, fontSize: 13, lineHeight: 1.5 },
  auditAction: { color: "#94a3b8", fontWeight: 500 },
  auditMeta: { color: "#334155", fontSize: 12 },
};

